import * as React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Reusable owner-collection editor (Multi-BDM M1), built generic on purpose: M2 needs exactly this
// control for FACILITY overrides, and writing it twice is how the two grains drift apart.
//
// It owns ONE invariant -- a non-empty collection always has exactly one primary -- and enforces it
// in `normalizeCollection` rather than in the UI, so the caller can never assemble an invalid value
// and the API's "primary_bdm_id is required" error is unreachable from this component.

// The sentinel option carries value="", which MUI renders as an EMPTY select display; displayEmpty
// plus a pinned label makes the empty state read as real text instead of an unlabelled box.
const EMPTY_SELECT_PROPS = {
  SelectProps: { displayEmpty: true, multiple: true },
  InputLabelProps: { shrink: true },
};

/**
 * The collection invariant, exported so it can be tested and reused directly.
 *   - duplicates removed;
 *   - empty collection => no primary (a valid state meaning "no owners");
 *   - non-empty collection => the primary is one of the members; if the previous primary is gone,
 *     the first remaining owner is promoted.
 */
export function normalizeCollection(bdmIds, primaryBdmId) {
  const ids = [...new Set((bdmIds || []).map(Number))];
  if (!ids.length) return { bdmIds: [], primaryBdmId: null };
  const primary = ids.includes(Number(primaryBdmId)) ? Number(primaryBdmId) : ids[0];
  return { bdmIds: ids, primaryBdmId: primary };
}

export default function OwnerCollectionEditor(props) {
  const bdmOptions = props.bdmOptions || [];
  const value = props.value || { bdmIds: [], primaryBdmId: null };
  const onChange = props.onChange;
  const disabled = props.disabled;
  const label = props.label || "Owners";
  const emptyLabel = props.emptyLabel || "— None —";
  const helperText = props.helperText;

  const selectedIds = value.bdmIds || [];
  const primaryBdmId = value.primaryBdmId ?? null;

  const nameById = new Map(bdmOptions.map((bdm) => [bdm.bdm_id, bdm.display_name]));

  const handleSelectChange = (event) => {
    const raw = event.target.value;
    const next = (Array.isArray(raw) ? raw : [raw]).map(Number);
    // Auto-promotion happens here: if the current primary was just deselected, normalizeCollection
    // promotes the first remaining owner rather than leaving the collection without one.
    onChange(normalizeCollection(next, primaryBdmId));
  };

  const handlePrimaryChange = (event) => {
    onChange(normalizeCollection(selectedIds, Number(event.target.value)));
  };

  // An INACTIVE BDM can never be newly selected, but one that is already assigned stays selectable
  // so it can be REMOVED -- the rule established for the facility dialog in A3.
  const ownerItems = bdmOptions.map((bdm) => (
    <MenuItem
      key={bdm.bdm_id}
      value={String(bdm.bdm_id)}
      disabled={!bdm.active && !selectedIds.includes(bdm.bdm_id)}
    >
      <Checkbox size="small" checked={selectedIds.includes(bdm.bdm_id)} />
      <ListItemText primary={bdm.active ? bdm.display_name : `${bdm.display_name} (inactive)`} />
    </MenuItem>
  ));

  const primaryRows = selectedIds.map((bdmId) => (
    <FormControlLabel
      key={bdmId}
      value={String(bdmId)}
      control={<Radio size="small" />}
      label={nameById.get(bdmId) || `BDM ${bdmId}`}
      disabled={disabled}
    />
  ));

  const renderSelected = (selected) => {
    if (!selected || !selected.length) return emptyLabel;
    const names = selected.map((id) => nameById.get(Number(id)) || `BDM ${id}`);
    return names.join(", ");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        select
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        helperText={helperText}
        value={selectedIds.map(String)}
        onChange={handleSelectChange}
        {...EMPTY_SELECT_PROPS}
        SelectProps={{ ...EMPTY_SELECT_PROPS.SelectProps, renderValue: renderSelected }}
        inputProps={{ "aria-label": label }}
      >
        {ownerItems}
      </TextField>

      {selectedIds.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Primary owner
          </Typography>
          <RadioGroup value={primaryBdmId == null ? "" : String(primaryBdmId)} onChange={handlePrimaryChange}>
            {primaryRows}
          </RadioGroup>
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">
          {props.emptyStateText || "No owners selected."}
        </Typography>
      )}
    </Box>
  );
}
